/**
 * Sprint 5-a-hotfix-2 · xlsx 進階工具 → Collabora postMessage 序列（純函式·可單元測試）。
 *
 * verify-before-trust（Cool UNO postMessage 實測結論）：
 * - apply_formula 走已證實原語 GoToCell + Action_Paste("=…")（Calc 把貼上的 "=" 文字解析為公式）。
 * - merge_cells / freeze_panes / format_cells 走非對話式 .uno: 指令（文件有列·待感官實測）。
 * - create_sheet 走 .uno:Insert（可能跳對話·#1 感官實測項）。
 * - insert_chart / conditional_format / data_validation 對話/精靈式·無法 headless → 不在此支援
 *   （backend 也未 register·AI 不會呼叫）。
 *
 * 回傳 postMessage payload 陣列（依序送出）；不支援的 tool 回 null（caller 視為無法落地）。
 */
export interface OfficePostMessage {
  MessageId: string;
  Values: Record<string, unknown>;
}

// 需要「結構化 postMessage 派送」的工具（非「貼內容字串」類）。
export const STRUCTURAL_OFFICE_TOOLS = new Set<string>([
  'create_sheet', 'apply_formula', 'merge_cells', 'format_cells', 'freeze_panes',
]);

const uno = (command: string, args?: Record<string, unknown>): OfficePostMessage => ({
  MessageId: 'Send_UNO_Command',
  Values: args ? { Command: command, Args: args } : { Command: command },
});

const gotoCell = (ref: string): OfficePostMessage =>
  uno('.uno:GoToCell', { ToPoint: { type: 'string', value: ref } });

const pasteText = (data: string): OfficePostMessage => ({
  MessageId: 'Action_Paste',
  Values: { Mimetype: 'text/plain;charset=utf-8', Data: data },
});

const NUMBER_FORMAT_UNO: Record<string, string> = {
  currency: '.uno:NumberFormatCurrency',
  percent: '.uno:NumberFormatPercent',
  date: '.uno:NumberFormatDate',
  thousands: '.uno:NumberFormatThousands',
};

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** 依 tool name + input 產生 postMessage 序列；不支援 → null。 */
export function buildOfficeActionMessages(
  name: string, input: Record<string, unknown>,
): OfficePostMessage[] | null {
  switch (name) {
    case 'apply_formula': {
      const cell = str(input.cell);
      const formula = str(input.formula);
      if (!cell || !formula) return null;
      // 選到目標 cell → 貼上公式字串（Calc 解析為公式·非文字）
      return [gotoCell(cell), pasteText(formula)];
    }
    case 'merge_cells': {
      const range = str(input.range);
      if (!range) return null;
      return [gotoCell(range), uno('.uno:MergeCells'), uno('.uno:AlignHorizontalCenter')];
    }
    case 'freeze_panes': {
      const cell = str(input.cell);
      if (!cell) return null;
      return [gotoCell(cell), uno('.uno:FreezePanes')];
    }
    case 'format_cells': {
      const range = str(input.range);
      const cmd = NUMBER_FORMAT_UNO[str(input.format)];
      if (!range || !cmd) return null;
      return [gotoCell(range), uno(cmd)];
    }
    case 'create_sheet': {
      const sheetName = str(input.sheet_name);
      if (!sheetName) return null;
      // hotfix-3 · Option A：.uno:Insert 帶「完整參數」試圖跳過 Insert-Sheet 對話框
      //（hotfix-2 只帶 Name 會跳對話·porsche 已 confirm）。Name+Index(末端)+Copy 齊全時
      //  部分 Cool 版本可 headless 建立。⚠️ 仍為感官實測項：若照舊跳對話 → 走 hotfix-3b(openpyxl)。
      // hotfix-3d · 建完再 JumpToTable 切換 focus 到新 sheet，後續 build_xlsx_table 才落在此 sheet
      //（lab 在 225 empirical 驗過此 activation 必要·#263 create_sheet 漏了 → 補上）。
      return [
        uno('.uno:Insert', {
          Name: { type: 'string', value: sheetName },
          Index: { type: 'long', value: 32767 },      // 大 index → 附加到最末
          Copy: { type: 'boolean', value: false },
        }),
        uno('.uno:JumpToTable', { Name: { type: 'string', value: sheetName } }),
      ];
    }
    default:
      return null;   // 非結構化 / 不支援 → caller 走內容字串路徑或視為無法落地
  }
}
